/* ===========================================================================
   Handora — Build Your Own Bundle
   ---------------------------------------------------------------------------
   Client state only. Nothing touches the Shopify cart until the shopper hits
   "Add bundle to cart", at which point every slot plus any add-ons go in as
   ONE /cart/add.js call (the theme's cart-sync patch trips a circuit breaker
   on rapid-fire adds, so one call it is).

   DUPLICATES ARE ALLOWED. A shopper buying five sets often wants two of the
   same design in different sizes, or two of the same set full stop — refusing
   the second tap cost a sale for no reason. Every add creates its own line
   with its own uid, so two lines of one product can carry different variants
   and be removed independently. The card button counts what is in the bundle
   ("ADD NOW ×2") and keeps adding; removal is the × on the slot.

   Bundle size: cfg.maxItems of 0 means UNLIMITED, and an unlimited rail has a
   problem — there is no finish line to draw. It is solved with a treadmill.
   Past the top tier the rail stops growing and starts MOVING: the fill runs
   forward to the marker standing ahead of her, that marker steps back into the
   reached slot carrying the number she just hit, and a fresh higher one drops
   in from off the end. Fixed geometry, endless ladder, and the sense of having
   just cleared something on every single add.

   What no marker may do is claim the next set unlocks something: at five sets
   the discount is already 30%, so the running note stays on "top tier
   unlocked". The treadmill sells momentum, not a promise.

   Tier maths note: only nail SETS count toward a tier or a free gift. Anything
   picked while the accessories collection is showing is stored as an add-on —
   those products carry the "Tools & Accessories" tag, which the Tier-Eligible
   Products collection excludes, so counting them would promise a discount the
   checkout refuses. Add-ons stay one-per-product: a second cuticle oil is a
   mistake, not a choice.

   Copy placeholders use [square brackets] — see snippets/hbb-fill.liquid.
   =========================================================================== */

(function () {
  'use strict';

  var BOOTED = 'hbbBooted';

  function money(cents, format) {
    var f = format || '${{amount}}';
    var rx = /\{\{\s*(\w+)\s*\}\}/;
    function withDelim(number, precision, thousands, decimal) {
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      var n = (number / 100).toFixed(precision);
      var parts = n.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    var m = f.match(rx);
    var val;
    switch (m && m[1]) {
      case 'amount_no_decimals': val = withDelim(cents, 0); break;
      case 'amount_with_comma_separator': val = withDelim(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': val = withDelim(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': val = withDelim(cents, 2, "'", '.'); break;
      default: val = withDelim(cents, 2);
    }
    return f.replace(rx, val);
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function fill(template, map) {
    var out = String(template == null ? '' : template);
    Object.keys(map).forEach(function (k) {
      out = out
        .replace(new RegExp('\\[\\s*' + k + '\\s*\\]', 'g'), map[k])
        .replace(new RegExp('\\{\\{\\s*' + k + '\\s*\\}\\}', 'g'), map[k]);
    });
    return out;
  }

  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  function parseJSONAttr(el, name, fallback) {
    try { return JSON.parse(el.getAttribute(name)); } catch (e) { return fallback; }
  }

  function isMobile() { return window.matchMedia('(max-width: 1099px)').matches; }

  /* The stylesheet flattens every transition inside .hbb under
     prefers-reduced-motion, but a WAAPI animation is invisible to CSS — it has
     to be asked directly, or the treadmill keeps sliding for someone who
     explicitly said no. */
  function stillness() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* Run through the Web Animations API rather than a CSS class. The badges are
     rebuilt with innerHTML on every render, so a class-driven keyframe would
     replay on unrelated re-renders — a variant change, a price refresh — and a
     badge that jumps for no reason is worse than one that never jumps. */
  function pop(li) {
    if (!li) return;
    var b = li.querySelector('.hbb-prog__badge') || li;
    if (!b.animate) return;
    b.animate([
      { transform: 'scale(1) translateY(0)' },
      { transform: 'scale(1.34) translateY(-5px)', offset: 0.35 },
      { transform: 'scale(0.93) translateY(1px)', offset: 0.7 },
      { transform: 'scale(1) translateY(0)' }
    ], { duration: 480, easing: 'cubic-bezier(.34,1.56,.64,1)' });
  }

  function init(root) {
    if (!root || root.dataset[BOOTED] === '1') return;
    root.dataset[BOOTED] = '1';

    var cfgNode = root.querySelector('[data-hbb-config]');
    if (!cfgNode) return;
    var cfg = JSON.parse(cfgNode.textContent);

    /* 0 (or anything below 1) means no ceiling at all */
    var CAPPED = Number(cfg.maxItems) > 0;
    var MAX = CAPPED ? Number(cfg.maxItems) : Infinity;

    var el = {
      grid: root.querySelector('[data-hbb-grid]'),
      more: root.querySelector('[data-hbb-more]'),
      moreBtn: root.querySelector('[data-hbb-more-btn]'),
      empty: root.querySelector('[data-hbb-empty]'),
      mode: root.querySelector('[data-hbb-mode]'),
      search: root.querySelector('[data-hbb-search]'),
      searchClear: root.querySelector('[data-hbb-search-clear]'),
      collectionSelect: root.querySelector('[data-hbb-collection-select]'),
      sortSelect: root.querySelector('[data-hbb-sort-select]'),
      dropLabel: root.querySelector('[data-hbb-drop-label]'),
      sortLabel: root.querySelector('[data-hbb-sort-label]'),
      picked: root.querySelector('[data-hbb-picked]'),
      slots: root.querySelector('[data-hbb-slots]'),
      progFill: root.querySelector('[data-hbb-prog-fill]'),
      progSteps: root.querySelector('[data-hbb-prog-steps]'),
      progLabels: root.querySelector('[data-hbb-prog-labels]'),
      say: root.querySelector('[data-hbb-say]'),
      count: root.querySelector('[data-hbb-count]'),
      sumRegular: root.querySelector('[data-hbb-regular]'),
      sumSave: root.querySelector('[data-hbb-save]'),
      sumSaveRow: root.querySelector('[data-hbb-save-row]'),
      sumTotal: root.querySelector('[data-hbb-total]'),
      ctaPrice: root.querySelector('[data-hbb-cta-price]'),
      err: root.querySelector('[data-hbb-err]'),
      panelHolder: root.querySelector('[data-hbb-panel-holder]'),
      panel: root.querySelector('.hbb-panel'),
      scrim: root.querySelector('[data-hbb-scrim]'),
      sizeWrap: root.querySelector('[data-hbb-size]'),
      toast: root.querySelector('[data-hbb-toast]'),
      top: root.querySelector('[data-hbb-top]')
    };

    var store = 'hbb:' + cfg.sectionId;
    var uid = 0;
    var state = {
      items: [],
      addons: [],
      prefs: {},
      collection: cfg.collections.length ? cfg.collections[0].handle : '',
      sort: cfg.defaultSort || '',
      query: '',
      next: null,
      loading: false,
      autoLoads: 0,
      lastTierQty: 0,
      lastCount: 0
    };

    function isAddonMode() {
      return !state.query && !!cfg.addonHandle && state.collection === cfg.addonHandle;
    }

    /* ------------------------------------------------------------ state -- */

    function save() {
      try {
        localStorage.setItem(store, JSON.stringify({
          items: state.items, addons: state.addons, prefs: state.prefs,
          collection: state.collection, sort: state.sort
        }));
      } catch (e) {}
    }

    function known(handle) {
      if (handle === cfg.addonHandle) return true;
      return cfg.collections.some(function (c) { return c.handle === handle; });
    }

    function restore() {
      var raw;
      try { raw = localStorage.getItem(store); } catch (e) { return; }
      if (!raw) return;
      try {
        var d = JSON.parse(raw);
        if (d && Array.isArray(d.items)) state.items = CAPPED ? d.items.slice(0, MAX) : d.items.slice();
        if (d && Array.isArray(d.addons)) state.addons = d.addons;
        if (d && d.prefs) state.prefs = d.prefs;
        if (d && typeof d.sort === 'string') state.sort = d.sort;
        if (d && d.collection && known(d.collection)) state.collection = d.collection;
      } catch (e) {}

      /* Bundles saved before duplicates were allowed have product-scoped keys.
         Restamp every line so keys are unique from here on, whatever they were. */
      state.items.forEach(function (it) { it.key = 'k' + (++uid); });

      state.lastTierQty = (tierFor(state.items.length) || { qty: 0 }).qty;
      /* a restored bundle is not a new milestone — do not animate on load */
      state.lastCount = state.items.length;
    }

    function wipe() { try { localStorage.removeItem(store); } catch (e) {} }

    /* ------------------------------------------------------------ tiers -- */

    function tierFor(n) {
      var best = null;
      cfg.tiers.forEach(function (t) { if (n >= t.qty && (!best || t.qty > best.qty)) best = t; });
      return best;
    }

    function nextTier(n) {
      var up = cfg.tiers.filter(function (t) { return t.qty > n; });
      up.sort(function (a, b) { return a.qty - b.qty; });
      return up[0] || null;
    }

    var TOP_QTY = 0;
    var TOP_PCT = 0;
    cfg.tiers.forEach(function (t) { if (t.qty > TOP_QTY) { TOP_QTY = t.qty; TOP_PCT = t.pct; } });

    var SCALE_QTY = CAPPED ? MAX : TOP_QTY + 1.4;
    var ROLL_POS = 95;
    var TOP_POS = TOP_QTY ? Math.min(100, (TOP_QTY / SCALE_QTY) * 100) : 0;

    function rollingTier() {
      if (CAPPED) return null;
      var n = state.items.length;
      if (!TOP_QTY || n < TOP_QTY) return null;
      return { qty: n + 1, pct: TOP_PCT, roll: true };
    }

    function ladder() {
      var list = cfg.tiers.slice();
      var roll = rollingTier();
      if (roll) list.push(roll);
      return list;
    }

    function tierPos(tier) {
      if (tier.roll) return ROLL_POS;
      return Math.min(100, (tier.qty / SCALE_QTY) * 100);
    }

    function fillPos() {
      var n = state.items.length;
      var cap = CAPPED ? MAX : TOP_QTY;
      return Math.min(100, (Math.min(n, cap) / SCALE_QTY) * 100);
    }

    /* ------------------------------------------------------- size prefs -- */

    function normKey(name) { return String(name || '').trim().toLowerCase(); }

    function matches(v, optNames, prefs, forced) {
      if (!v) return false;
      var ok = true;
      optNames.forEach(function (n, i) {
        var k = normKey(n);
        var want = (forced && forced[k] != null) ? forced[k] : prefs[k];
        if (want && String(v.o[i]).toLowerCase() !== String(want).toLowerCase()) ok = false;
      });
      return ok;
    }

    function pickVariant(variants, optNames, prefs, forced) {
      var avail = variants.filter(function (v) { return v.a; });
      if (!avail.length) return null;
      var best = null;
      var bestScore = -1;
      avail.forEach(function (v) {
        var score = 0;
        optNames.forEach(function (n, i) {
          var k = normKey(n);
          var want = (forced && forced[k] != null) ? forced[k] : prefs[k];
          if (!want) return;
          if (String(v.o[i]).toLowerCase() === String(want).toLowerCase()) {
            score += (forced && forced[k] != null) ? 10 : (k === 'size' ? 3 : 2);
          }
        });
        if (score > bestScore) { bestScore = score; best = v; }
      });
      return { variant: best, exact: matches(best, optNames, prefs, forced) };
    }

    /* ------------------------------------------------------------- grid -- */

    function skeletons(n) {
      var out = '';
      for (var i = 0; i < n; i++) out += '<div class="hbb-skel"></div>';
      return out;
    }

    function gridUrl(page) {
      if (state.query) {
        return cfg.routes.search +
          '?q=' + encodeURIComponent(state.query) +
          '&type=product&options%5Bprefix%5D=last' +
          '&section_id=' + cfg.gridSection +
          '&page=' + page;
      }
      return cfg.routes.root + 'collections/' + state.collection +
        '?section_id=' + cfg.gridSection +
        '&page=' + page +
        (state.sort ? '&sort_by=' + encodeURIComponent(state.sort) : '');
    }

    function loadGrid(page, append) {
      if (state.loading) return;
      if (!state.query && !state.collection) return;
      state.loading = true;

      if (!append) {
        state.autoLoads = 0;
        el.grid.innerHTML = skeletons(6);
        el.more.hidden = true;
        el.empty.hidden = true;
      } else if (el.moreBtn) {
        el.moreBtn.textContent = cfg.strings.loading;
      }

      fetch(gridUrl(page), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cards = doc.querySelector('[data-hbb-cards]');
          var incoming = cards ? cards.innerHTML : '';
          if (!append) el.grid.innerHTML = '';
          if (incoming.trim()) el.grid.insertAdjacentHTML('beforeend', incoming);
          state.next = cards && cards.getAttribute('data-next-page')
            ? parseInt(cards.getAttribute('data-next-page'), 10)
            : null;
          el.more.hidden = !state.next;
          if (el.moreBtn) el.moreBtn.textContent = cfg.strings.loadMore;
          var none = !el.grid.querySelector('.hbb-card');
          el.empty.textContent = state.query ? cfg.strings.searchEmpty : (el.empty.getAttribute('data-default') || el.empty.textContent);
          el.empty.hidden = !none;
          paintCards();
        })
        .catch(function () {
          if (!append) el.grid.innerHTML = '';
          el.empty.hidden = false;
          if (el.moreBtn) el.moreBtn.textContent = cfg.strings.loadMore;
        })
        .then(function () { state.loading = false; });
    }

    function countOf(productId) {
      var n = 0;
      state.items.forEach(function (i) { if (String(i.productId) === String(productId)) n++; });
      return n;
    }

    function hasAddon(productId) {
      return state.addons.some(function (a) { return String(a.productId) === String(productId); });
    }

    /* The set button never becomes a dead "Added" state — it keeps offering the
       action and reports the count beside it, because a shopper who wants two
       of a design should not have to guess whether the button still works. */
    function paintCards() {
      var addonMode = isAddonMode();
      var full = state.items.length >= MAX;

      Array.prototype.forEach.call(el.grid.querySelectorAll('[data-hbb-card]'), function (card) {
        var pid = card.getAttribute('data-product-id');
        var count = addonMode ? (hasAddon(pid) ? 1 : 0) : countOf(pid);
        var added = count > 0;

        card.classList.toggle('is-added', added);

        var btn = card.querySelector('[data-hbb-add-card]');
        if (!btn) return;

        if (addonMode) {
          btn.textContent = added ? cfg.strings.added : cfg.strings.addOn;
          btn.disabled = false;
        } else {
          btn.textContent = added ? (cfg.strings.addNow + ' ×' + count) : cfg.strings.addNow;
          btn.disabled = full;
        }

        var note = card.querySelector('[data-hbb-card-note]');
        if (note && !added) note.textContent = '';
      });
    }

    /* ---------------------------------------------------------- actions -- */

    function addFromCard(card) {
      var productId = card.getAttribute('data-product-id');
      var variants = parseJSONAttr(card, 'data-variants', []);
      var optNames = parseJSONAttr(card, 'data-optnames', []);

      if (isAddonMode()) {
        if (hasAddon(productId)) {
          state.addons = state.addons.filter(function (a) { return String(a.productId) !== String(productId); });
          render();
          return;
        }
        var ares = pickVariant(variants, optNames, {}, null);
        if (!ares || !ares.variant) { toast(cfg.strings.soldOut); return; }
        state.addons.push({
          productId: productId,
          variantId: ares.variant.id,
          title: card.getAttribute('data-title'),
          price: ares.variant.p, compare: ares.variant.c, image: card.getAttribute('data-image')
        });
        render();
        if (isMobile()) toast(cfg.strings.addedToast);
        return;
      }

      if (state.items.length >= MAX) { toast(cfg.strings.full); return; }

      var res = pickVariant(variants, optNames, state.prefs, null);
      if (!res || !res.variant) { toast(cfg.strings.soldOut); return; }

      var v = res.variant;
      state.items.push({
        /* uid, not productId-variantId: two lines of the same set in the same
           size must still be told apart, or removing one removes both and
           changing a size changes both. */
        key: 'k' + (++uid),
        productId: productId,
        variantId: v.id,
        title: card.getAttribute('data-title'),
        url: card.getAttribute('data-url'),
        image: card.getAttribute('data-image'),
        price: v.p,
        compare: v.c,
        optNames: optNames,
        opts: v.o.slice(),
        variants: variants
      });

      var note = card.querySelector('[data-hbb-card-note]');
      if (note && !res.exact) note.textContent = fill(cfg.strings.sizeSwapped, { variant: v.t });

      render();
      if (isMobile()) toast(cfg.strings.addedToast);
    }

    function removeByKey(key) {
      state.items = state.items.filter(function (i) { return i.key !== key; });
      render();
    }

    function setVariant(itemKey, variantId) {
      var item = state.items.filter(function (i) { return i.key === itemKey; })[0];
      if (!item) return;
      var v = item.variants.filter(function (x) { return String(x.id) === String(variantId); })[0];
      if (!v || !v.a) { toast(cfg.strings.comboOut); return; }
      item.variantId = v.id;
      item.opts = v.o.slice();
      item.price = v.p;
      item.compare = v.c;
      /* the key identifies the LINE, not the variant — leave it alone */
      render();
    }

    function toast(msg) {
      if (!el.toast || !msg) return;
      el.toast.textContent = msg;
      el.toast.classList.add('is-on');
      clearTimeout(el.toast._t);
      el.toast._t = setTimeout(function () { el.toast.classList.remove('is-on'); }, 2200);
    }

    /* ----------------------------------------------------------- render -- */

    function totals() {
      var sets = 0;
      state.items.forEach(function (i) { sets += i.price; });
      var extras = 0;
      state.addons.forEach(function (a) { extras += a.price; });
      var tier = tierFor(state.items.length);
      var pct = tier ? tier.pct : 0;
      var off = Math.round(sets * pct / 100);
      return {
        sets: sets, extras: extras, regular: sets + extras,
        tier: tier, pct: pct, off: off, total: sets + extras - off
      };
    }

    /* -------------------------------------------------------- treadmill -- */

    var rollBusy = false;
    var rollToken = 0;
    var rollTimers = [];

    function later(fn, ms) { rollTimers.push(setTimeout(fn, ms)); }

    function stopRoll() {
      rollToken++;
      rollBusy = false;
      rollTimers.forEach(function (t) { clearTimeout(t); });
      rollTimers = [];
      el.progFill.style.transition = '';
    }

    /* One turn of the treadmill, in two beats.

       Beat one: the fill runs forward off the top tier and out to the marker
       waiting ahead of her. That marker is already labelled with the count she
       has just reached — it was drawn one add ago — so it lights up the moment
       the bar touches it. Nothing is relabelled yet; the number she watches
       arrive is the number she earned.

       Beat two: the rail steps back one notch. The marker she just cleared
       slides into the reached slot keeping its number, a higher one drops in
       from beyond the end, and the fill retracts with them. Geometry is back
       where it started and there is a new thing to chase.

       Positions animate through `left`, never `transform` — the steps and
       labels are centred with translateX(-50%), and a transform keyframe would
       knock every badge half a badge to the right. */
    function conveyor() {
      var rail = el.progSteps.offsetWidth;
      if (!rail || !el.progSteps.animate) { paintProgress(); return; }

      var token = ++rollToken;
      var span = ROLL_POS - TOP_POS;
      var ease = { duration: 520, easing: 'cubic-bezier(.22,1,.36,1)' };
      var lead = el.progSteps.querySelector('[data-hbb-step="roll"]');
      rollBusy = true;

      el.progFill.style.transition = 'width 380ms cubic-bezier(.32,0,.28,1)';
      el.progFill.style.width = ROLL_POS + '%';

      later(function () {
        if (token !== rollToken) return;
        if (lead) { lead.classList.add('is-reached'); pop(lead); }
      }, 330);

      later(function () {
        if (token !== rollToken) return;

        paintProgress(true);

        var back = [
          el.progSteps.querySelector('[data-hbb-step="top"]'),
          el.progLabels.querySelector('[data-hbb-lab="top"]')
        ];
        var incoming = [
          el.progSteps.querySelector('[data-hbb-step="roll"]'),
          el.progLabels.querySelector('[data-hbb-lab="roll"]')
        ];

        back.forEach(function (node) {
          if (!node || !node.animate) return;
          node.animate([{ left: ROLL_POS + '%' }, { left: TOP_POS + '%' }], ease);
        });

        incoming.forEach(function (node) {
          if (!node || !node.animate) return;
          node.animate([
            { left: (ROLL_POS + span) + '%', opacity: 0 },
            { left: ROLL_POS + '%', opacity: 1 }
          ], ease);
        });

        el.progFill.style.transition = 'none';
        el.progFill.style.width = ROLL_POS + '%';
        void el.progFill.offsetWidth;
        el.progFill.style.transition = 'width 520ms cubic-bezier(.22,1,.36,1)';
        el.progFill.style.width = fillPos() + '%';

        later(function () {
          if (token !== rollToken) return;
          rollBusy = false;
          el.progFill.style.transition = '';
        }, 540);
      }, 470);
    }

    function renderProgress() {
      var n = state.items.length;
      var grew = n > state.lastCount;
      var past = !CAPPED && TOP_QTY > 0 && n > TOP_QTY && state.lastCount >= TOP_QTY;

      /* A second add landing mid-turn cancels the animation instead of queueing
         it — someone tapping fast wants the count, not a show. */
      if (rollBusy) { stopRoll(); paintProgress(); return; }

      if (grew && past && !stillness() && el.progSteps.querySelector('[data-hbb-step="roll"]')) {
        conveyor();
        return;
      }

      paintProgress();
    }

    function paintProgress(quiet) {
      var n = state.items.length;
      var nt = nextTier(n);
      var reached = tierFor(n);
      var roll = rollingTier();
      var justHit = reached && reached.qty > state.lastTierQty ? reached.qty : 0;
      var grew = n > state.lastCount;
      var nextQty = nt ? nt.qty : (roll ? roll.qty : 0);

      /* Past the top tier the 30% badge stops saying "5 sets" and starts saying
         where she actually is. The badge marks the tier she is standing on, so
         once she is past the entry price of that tier the useful number is her
         own count, not the threshold she cleared several sets ago. */
      var liveTop = !CAPPED && TOP_QTY > 0 && n > TOP_QTY;

      el.progFill.style.width = fillPos() + '%';

      var badges = '';
      var labels = '';
      ladder().forEach(function (tier) {
        var isTop = !tier.roll && tier.qty === TOP_QTY;
        var shownQty = tier.roll ? tier.qty : ((isTop && liveTop) ? n : tier.qty);
        var mark = tier.roll ? 'roll' : (isTop ? 'top' : String(tier.qty));
        var left = tierPos(tier);

        var cls = 'hbb-prog__step';
        if (n >= tier.qty) cls += ' is-reached';
        if (nextQty === tier.qty) cls += ' is-next';
        if (justHit === tier.qty) cls += ' is-hit';

        badges += '<li class="' + cls + '" data-hbb-step="' + mark + '" style="left:' + left + '%">' +
          '<span class="hbb-prog__badge">' + tier.pct + '%</span></li>';
        labels += '<li class="hbb-prog__label' + (n >= tier.qty ? ' is-reached' : '') + '" data-hbb-lab="' + mark + '" style="left:' + left + '%">' +
          esc(fill(cfg.strings.itemsLabel, { count: shownQty })) + '</li>';
      });
      el.progSteps.innerHTML = badges;
      el.progLabels.innerHTML = labels;

      /* quiet = the treadmill is mid-turn and has already done the celebrating;
         popping again here would double-bounce the badge it just landed on. */
      if (grew && !quiet) {
        if (justHit) {
          pop(el.progSteps.querySelector('[data-hbb-step="' + justHit + '"]'));
          if (justHit === TOP_QTY) pop(el.progSteps.querySelector('[data-hbb-step="top"]'));
        } else if (liveTop) {
          pop(el.progSteps.querySelector('[data-hbb-step="top"]'));
        }
      }

      state.lastTierQty = reached ? reached.qty : 0;
      state.lastCount = n;

      /* The rolling badge is never allowed to speak. Telling someone at five
         sets to "add 1 more to unlock 30%" when she already has 30% is simply
         untrue, and a shopper who checks the maths stops believing the rest of
         the panel. */
      if (el.say) {
        if (n === 0) {
          el.say.innerHTML = esc(fill(cfg.strings.noteEmpty, { qty: cfg.lowestQty, pct: cfg.lowestPct + '%' }));
        } else if (nt) {
          el.say.innerHTML = esc(fill(cfg.strings.noteNext, { more: nt.qty - n, pct: nt.pct + '%' }))
            .replace(nt.pct + '%', '<b>' + nt.pct + '%</b>');
        } else {
          el.say.innerHTML = esc(fill(cfg.strings.noteMax, { pct: (reached ? reached.pct : 0) + '%' }));
        }
      }
    }

    function slotOptionsHTML(item) {
      var avail = item.variants.filter(function (v) { return v.a; });
      if (avail.length < 2) return '';
      var out = '<select class="hbb-slot__select" data-hbb-variant="' + esc(item.key) + '" aria-label="' + esc(item.optNames.join(' / ')) + '">';
      avail.forEach(function (v) {
        var on = String(v.id) === String(item.variantId);
        out += '<option value="' + v.id + '"' + (on ? ' selected' : '') + '>' + esc(v.t) + '</option>';
      });
      out += '</select>';
      return out;
    }

    function slotRows() {
      var n = state.items.length;
      if (CAPPED) return MAX;
      return Math.max(n + 1, TOP_QTY);
    }

    function renderSlots() {
      var html = '';
      var rows = slotRows();
      for (var i = 0; i < rows; i++) {
        var item = state.items[i];
        if (item) {
          var was = (item.compare && item.compare > item.price)
            ? '<span class="hbb-slot__was">' + money(item.compare, cfg.moneyFormat) + '</span>' : '';
          html += '<li class="hbb-slot">' +
            '<span class="hbb-slot__thumb">' + (item.image ? '<img src="' + esc(item.image) + '" alt="" loading="lazy" width="56" height="62">' : '') + '</span>' +
            '<span class="hbb-slot__body">' +
              '<span class="hbb-slot__name">' + esc(item.title) + '</span>' +
              '<span class="hbb-slot__opts">' + slotOptionsHTML(item) + '</span>' +
            '</span>' +
            '<span class="hbb-slot__price">' + money(item.price, cfg.moneyFormat) + was + '</span>' +
            '<button type="button" class="hbb-slot__remove" data-hbb-remove="' + esc(item.key) + '" aria-label="' + esc(cfg.strings.remove) + '">&times;</button>' +
          '</li>';
        } else {
          var slotQty = i + 1;
          var tierHere = null;
          cfg.tiers.forEach(function (t) { if (t.qty === slotQty) tierHere = t; });
          var hint = tierHere
            ? '<span class="hbb-slot__hint">' + esc(fill(cfg.strings.unlock, { qty: tierHere.qty, pct: tierHere.pct + '%' })) + '</span>'
            : '';
          html += '<li class="hbb-slot hbb-slot--empty">' +
            '<span class="hbb-slot__thumb">+</span>' +
            '<span class="hbb-slot__body">' +
              '<span class="hbb-slot__name">' + esc(cfg.strings.selectProduct) + '</span>' + hint +
            '</span>' +
          '</li>';
        }
      }
      el.slots.innerHTML = html;

      if (!el.picked) return;
      var p = '';
      state.addons.forEach(function (a) {
        p += '<li class="hbb-slot hbb-slot--addon">' + '<span class="hbb-slot__thumb">' + (a.image ? '<img src="' + esc(a.image) + '" alt="" loading="lazy" width="56" height="62">' : '') + '</span>' +
          '<span class="hbb-slot__body"><span class="hbb-slot__name">' + esc(a.title) + '</span><span class="hbb-slot__hint">Add-on</span></span>' +
          '<span class="hbb-slot__price">' + money(a.price, cfg.moneyFormat) + ((a.compare && a.compare > a.price) ? '<span class="hbb-slot__was">' + money(a.compare, cfg.moneyFormat) + '</span>' : '') + '</span>' +
          '<button type="button" class="hbb-slot__remove" data-hbb-remove-addon="' + esc(a.productId) + '" aria-label="' + esc(cfg.strings.remove) + '">&times;</button>' +
        '</li>';
      });
      el.picked.innerHTML = p;
    }

    function renderGifts() {
      var n = state.items.length;
      Array.prototype.forEach.call(root.querySelectorAll('[data-hbb-gift]'), function (g) {
        var need = parseInt(g.getAttribute('data-qty'), 10) || 0;
        var worth = parseInt(g.getAttribute('data-worth'), 10) || 0;
        var on = n >= need;
        g.classList.toggle('is-on', on);
        var st = g.querySelector('[data-hbb-gift-state]');
        var flag = g.querySelector('[data-hbb-gift-flag]');
        if (st) {
          st.textContent = on
            ? fill(cfg.strings.giftUnlocked, { worth: money(worth, cfg.moneyFormat) })
            : fill(cfg.strings.giftLocked, { more: need - n });
        }
        if (flag) flag.textContent = on ? cfg.strings.giftFree : need + '+';
      });
    }

    function render() {
      var t = totals();
      var n = state.items.length;

      el.count.textContent = CAPPED
        ? (n + ' / ' + MAX)
        : fill(cfg.strings.itemsLabel, { count: n });

      renderProgress();
      renderSlots();
      renderGifts();

      el.sumRegular.textContent = money(t.regular, cfg.moneyFormat);
      el.sumSave.textContent = '-' + money(t.off, cfg.moneyFormat) + ' (' + t.pct + '%)';
      el.sumSaveRow.hidden = t.off <= 0;
      el.sumTotal.textContent = money(t.total, cfg.moneyFormat);
      if (el.ctaPrice) el.ctaPrice.textContent = t.total > 0 ? money(t.total, cfg.moneyFormat) : '';

      Array.prototype.forEach.call(root.querySelectorAll('[data-hbb-add]'), function (b) {
        b.disabled = (n + state.addons.length) < cfg.minItems;
      });

      paintCards();
      save();
      dock();
    }

    /* ------------------------------------------------------ add to cart -- */

    function fail(msg) {
      if (!el.err) return;
      el.err.hidden = false;
      el.err.textContent = msg || cfg.strings.error;
    }

    function openDrawer() {
      if (window.cart && typeof window.cart.getCart === 'function') {
        try { window.cart.getCart(); } catch (e) {}
      }
      var toggle = document.querySelector(
        '[data-cart-drawer-toggle], [data-cart-toggle], .cart__toggle, [data-drawer-toggle="cart"], .js-drawer-open-cart, a[href$="/cart"].site-nav__link'
      );
      if (!toggle) return false;
      toggle.click();
      return true;
    }

    function addBundle(btn) {
      if (!state.items.length && !state.addons.length) return;
      if (el.err) el.err.hidden = true;

      var t = totals();
      var bundleId = 'hbb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
      var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-hbb-add]'));
      buttons.forEach(function (b) { b.classList.add('is-busy'); b.disabled = true; });
      var labelNode = btn ? btn.querySelector('[data-hbb-cta-label]') : null;
      var label = labelNode ? labelNode.textContent : '';
      if (labelNode) labelNode.textContent = cfg.strings.adding;

      /* Two lines of the same variant carry identical properties, so Shopify
         merges them into one line of quantity 2 — which is exactly right. */
      var lines = state.items.map(function (i) {
        return {
          id: i.variantId,
          quantity: 1,
          properties: { _hbb: bundleId, _hbb_tier: t.tier ? t.tier.pct + '%' : 'none' }
        };
      }).concat(state.addons.map(function (a) {
        return { id: a.variantId, quantity: 1, properties: { _hbb: bundleId, _hbb_addon: 'yes' } };
      }));

      fetch(cfg.routes.add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines })
      })
        .then(function (r) {
          return r.json().then(function (data) {
            if (!r.ok) throw new Error(data && data.description ? data.description : cfg.strings.error);
            return data;
          });
        })
        .then(function () {
          return fetch(cfg.routes.cart, { headers: { Accept: 'application/json' } }).then(function () { return sleep(600); });
        })
        .then(function () {
          wipe();

          if (cfg.redirect === 'drawer' && openDrawer()) {
            state.items = [];
            state.addons = [];
            state.lastTierQty = 0;
            state.lastCount = 0;
            stopRoll();
            buttons.forEach(function (b) { b.classList.remove('is-busy'); });
            if (labelNode && label) labelNode.textContent = label;
            sheet(false);
            render();
            return;
          }

          window.location.href = cfg.redirect === 'drawer'
            ? cfg.routes.cart.replace(/\.js$/, '')
            : cfg.redirect;
        })
        .catch(function (e) {
          buttons.forEach(function (b) { b.classList.remove('is-busy'); b.disabled = false; });
          if (labelNode && label) labelNode.textContent = label;
          fail(e && e.message);
        });
    }

    /* ----------------------------------------------------------- the bar -- */

    function bare(text) {
      return String(text || '').replace(/\s*\([\d,.]+\)\s*$/, '').trim();
    }

    function markBar() {
      var h = state.query ? '' : state.collection;

      Array.prototype.forEach.call(root.querySelectorAll('[data-hbb-pick]'), function (b) {
        b.classList.toggle('is-active', !!h && b.getAttribute('data-hbb-pick') === h);
      });

      var inSelect = false;
      if (el.collectionSelect) {
        var opts = el.collectionSelect.options;
        var chosen = null;
        for (var i = 0; i < opts.length; i++) {
          if (opts[i].value && opts[i].value === h) { chosen = opts[i]; break; }
        }
        inSelect = !!chosen;
        el.collectionSelect.value = chosen ? chosen.value : '';
        if (el.dropLabel) el.dropLabel.textContent = chosen ? bare(chosen.textContent) : cfg.otherLabel;
      }

      var pill = root.querySelector('[data-hbb-collections]');
      if (pill) pill.classList.toggle('is-active', inSelect);

      if (el.sortSelect) {
        el.sortSelect.value = state.sort;
        var so = el.sortSelect.options[el.sortSelect.selectedIndex];
        if (so && el.sortLabel) el.sortLabel.textContent = bare(so.textContent);
      }

      if (el.mode) el.mode.hidden = !isAddonMode();
      if (el.searchClear) el.searchClear.hidden = !state.query;
    }

    function pickCollection(handle) {
      if (!handle) { markBar(); return; }
      if (handle === state.collection && !state.query) { markBar(); return; }
      state.collection = handle;
      state.query = '';
      if (el.search) el.search.value = '';
      markBar();
      loadGrid(1, false);
      save();
    }

    var searchTimer = null;
    function onSearchInput() {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(function () {
        var q = (el.search.value || '').trim();
        if (q.length === 1) return;
        if (q === state.query) return;
        state.query = q;
        markBar();
        loadGrid(1, false);
      }, 380);
    }

    /* ------------------------------------------------- panel + chrome ---- */

    function sheet(open) {
      if (!el.panelHolder) return;
      el.panelHolder.classList.toggle('is-open', !!open);
      if (el.scrim) el.scrim.classList.toggle('is-open', !!open);
    }

    function dock() {
      if (!el.panel) return;
      if (!isMobile()) { root.style.setProperty('--hbb-bar-h', '0px'); return; }
      if (el.panelHolder.classList.contains('is-open')) return;
      root.style.setProperty('--hbb-bar-h', Math.round(el.panel.offsetHeight) + 'px');
    }

    /* ----------------------------------------------------------- events -- */

    root.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      var pick = t.closest('[data-hbb-pick]');
      if (pick) { e.preventDefault(); pickCollection(pick.getAttribute('data-hbb-pick')); return; }

      if (t.closest('[data-hbb-search-clear]')) {
        e.preventDefault();
        el.search.value = '';
        state.query = '';
        markBar();
        loadGrid(1, false);
        el.search.focus();
        return;
      }

      /* Always an add. The card no longer doubles as a remove control: once a
         product can be in the bundle twice, "click the card to take it out"
         has no answer to the question of which one. Removal is the × on the
         slot, which points at exactly one line. Accessories still toggle —
         they are one-per-product, so there is no ambiguity there. */
      var addCard = t.closest('[data-hbb-add-card]');
      if (addCard) {
        e.preventDefault();
        var card = addCard.closest('[data-hbb-card]');
        if (card) addFromCard(card);
        return;
      }

      var rmAddon = t.closest('[data-hbb-remove-addon]');
      if (rmAddon) {
        e.preventDefault();
        var pid = rmAddon.getAttribute('data-hbb-remove-addon');
        state.addons = state.addons.filter(function (a) { return String(a.productId) !== String(pid); });
        render();
        return;
      }

      var rm = t.closest('[data-hbb-remove]');
      if (rm) { e.preventDefault(); removeByKey(rm.getAttribute('data-hbb-remove')); return; }

      var sizeOpt = t.closest('[data-hbb-size-opt]');
      if (sizeOpt) {
        e.preventDefault();
        var key = sizeOpt.getAttribute('data-hbb-size-key');
        var val = sizeOpt.getAttribute('data-hbb-size-opt');
        state.prefs[key] = state.prefs[key] === val ? '' : val;
        Array.prototype.forEach.call(el.sizeWrap.querySelectorAll('[data-hbb-size-key="' + key + '"]'), function (b) {
          b.classList.toggle('is-active', b.getAttribute('data-hbb-size-opt') === state.prefs[key]);
        });
        save();
        return;
      }

      if (t.closest('[data-hbb-more-btn]')) {
        e.preventDefault();
        if (state.next) loadGrid(state.next, true);
        return;
      }

      var sumToggle = t.closest('[data-hbb-sumtoggle]');
      if (sumToggle) {
        e.preventDefault();
        var sum = root.querySelector('[data-hbb-sum]');
        if (sum) {
          var openSum = sum.hidden;
          sum.hidden = !openSum;
          sumToggle.setAttribute('aria-expanded', openSum ? 'true' : 'false');
          dock();
        }
        return;
      }

      if (t.closest('[data-hbb-add]')) {
        e.preventDefault();
        addBundle(t.closest('[data-hbb-add]'));
        return;
      }

      if (t.closest('[data-hbb-clear]')) {
        e.preventDefault();
        state.items = [];
        state.addons = [];
        state.lastTierQty = 0;
        state.lastCount = 0;
        stopRoll();
        wipe();
        render();
        return;
      }

      if (t.closest('[data-hbb-head]')) {
        if (!isMobile()) return;
        e.preventDefault();
        sheet(!el.panelHolder.classList.contains('is-open'));
        return;
      }

      if (t.closest('[data-hbb-scrim]')) { e.preventDefault(); sheet(false); return; }

      if (t.closest('[data-hbb-top]')) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    });

    root.addEventListener('change', function (e) {
      var t = e.target;
      if (!t) return;

      if (t === el.collectionSelect) { pickCollection(t.value); return; }

      if (t === el.sortSelect) {
        state.sort = t.value;
        markBar();
        if (!state.query) loadGrid(1, false);
        save();
        return;
      }

      var sel = t.closest ? t.closest('[data-hbb-variant]') : null;
      if (sel) setVariant(sel.getAttribute('data-hbb-variant'), sel.value);
    });

    if (el.search) {
      el.search.addEventListener('input', onSearchInput);
      el.search.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); clearTimeout(searchTimer); onSearchInput(); }
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') sheet(false);
    });

    if ('IntersectionObserver' in window && el.more) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && state.next && !state.loading && state.autoLoads < 4) {
            state.autoLoads++;
            loadGrid(state.next, true);
          }
        });
      }, { rootMargin: '400px 0px' });
      io.observe(el.more);
    }

    var lastY = 0;
    window.addEventListener('scroll', function () {
      var y = window.pageYOffset || document.documentElement.scrollTop;
      if (el.top && Math.abs(y - lastY) > 40) {
        el.top.classList.toggle('is-on', y > 700);
        lastY = y;
      }
    }, { passive: true });

    window.addEventListener('resize', dock);

    /* ------------------------------------------------------------- boot -- */

    restore();

    if (el.empty) el.empty.setAttribute('data-default', el.empty.textContent);

    if (el.sizeWrap) {
      Array.prototype.forEach.call(el.sizeWrap.querySelectorAll('[data-hbb-size-opt]'), function (b) {
        var k = b.getAttribute('data-hbb-size-key');
        b.classList.toggle('is-active', state.prefs[k] === b.getAttribute('data-hbb-size-opt'));
      });
    }

    markBar();
    render();
    loadGrid(1, false);
    setTimeout(dock, 300);
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-hbb-root]'), init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', function (e) {
    var r = e.target.querySelector ? e.target.querySelector('[data-hbb-root]') : null;
    if (r) init(r);
  });
})();
